import React, { useEffect, useRef, useState } from 'react'
import { updateUser } from 'containers/Login/loginSlice'
import ArrowBtn from 'components/buttons/ArrowBtn'
import useTranslation from 'hooks/useTranslation'
import BackBtn from 'components/buttons/BackBtn'
import { GET, POST } from 'rest/request'
import Loading from 'components/Loading'
import { useReduxDispatch } from 'store'
import { AuthUserConsent } from 'Auth'
import * as urls from 'rest/urls'
import Popup from './Popup'

interface ConsentPopupProps {
  consent: AuthUserConsent
  close: basic.BasicFunc
}

const ConsentPopup: React.FC<ConsentPopupProps> = ({ consent, close }) => {
  const [isLoading, setLoading] = useState(true)
  const [isSending, setSending] = useState(false)
  const iframeRef = useRef<HTMLIFrameElement>(null)

  const dispatch = useReduxDispatch()
  const { t } = useTranslation('consent')

  const title = (() => {
    switch (consent.consentType) {
      case 'terms_accept':
        return consent.isCurrentAccepted ? t('terms') : t('termsUpdate')
      case 'privacy_accept':
        return consent.isCurrentAccepted ? t('privacyPolicy') : t('privacyPolicyUpdate')
      default:
        return consent.isCurrentAccepted ? t('marketingConsents') : t('marketingConsentsUpdate')
    }
  })()

  const acceptConsent = async () => {
    if (isLoading) return
    setSending(true)
    try {
      const body = { [consent.consentType]: 1 }
      const response = await POST(urls.user, { body })
      if (response.data?.consents) {
        dispatch(updateUser({ consents: response.data.consents }))
        close()
      }
      setSending(false)
    } catch (error) {
      setSending(false)
    }
  }

  const getDocument = async (url: string) => {
    try {
      const response = await GET(url)
      setLoading(false)
      if (typeof response === 'string') {
        const contentDocument = iframeRef.current?.contentDocument
        if (contentDocument) {
          contentDocument.open()
          contentDocument.write(response)
          contentDocument.close()
        }
      }
    } catch (error) {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (consent.contentUrl) {
      getDocument(consent.contentUrl)
    } else {
      setLoading(false)
    }
  }, [consent.contentUrl])

  return (
    <Popup
      centered
      close={close}>
      <div className='popup--consent'>
        <div className='popup__title'>
          <div>{title}</div>
        </div>
        <div className='popup__frame__container'>
          <iframe
            title={title}
            ref={iframeRef}
          />
          {isLoading && <Loading middleOfPage />}
        </div>
        {consent.isCurrentAccepted
          ? <div className='popup__buttons'>
            <BackBtn action={close} />
          </div>
          : <div className='popup__buttons'>
            <ArrowBtn
              enlarged
              text={t('accept')}
              isLoading={isSending}
              action={acceptConsent} />
            <BackBtn action={close} />
          </div>}
      </div>
    </Popup>
  )
}

export default ConsentPopup